import React, { useState, useEffect } from "react"
import Link from "next/link"
import classnames from "classnames"
import { Row, Col } from "react-bootstrap"
import { linksBuilder } from "../../../services/utils/linksBuilder"
import { useArtworksAction } from "../../actions/useArtworksAction"
import { GalleryHeader } from "../../components/GalleryHeader"
import Container from "../../components/Layout/Container"
import { HeadingTitle } from "../../components/Layout/HeadingTitle"
import { HelmetTitle } from "../../components/Layout/HelmetTitle"
import MobileMenu from "../../components/Layout/MobileMenu"
import { SearchSortHeader } from "../../components/Layout/SearchSortHeader"
import Wrapper from "../../components/Layout/Wrapper"
import LeftSide from "../../components/LeftSide"
import { Loader } from "../../components/Layout/Loader"
import { Masonry } from "../../components/Masonry"
import { ApplyButton } from "./ApplyButton"
import { FilterButton } from "./FilterButton"
import styles from "./ArtworksPage.module.scss"
import { SeoTags } from "../../components/Layout/SeoTags"
import { useAppContext } from "../../providers/AppProvider"
import { useContactAction } from "../../actions/useContactAction"
import { GalleryContactModal } from "../../components/GalleryContactModal"
import { getArtworkSize } from "../../../services/utils/getArtworkSize"
import { getArtworkTitle } from "../../../services/utils/getArtworkTitle"
import { getArtworkSubtitle } from "../../../services/utils/getArtworkSubtitle"
import { formatPrice } from "../../../services/utils/formatPrice"
import NewsletterGalleries from "../../components/NewsletterGalleries"

const Artworks = ({ catalog, gallery, filters }) => {
  const { profile } = useAppContext()
  const [catalogData, setCatalogData] = useState(catalog)
  const {
    loading,
    search,
    collapses,
    clearFilters,
    onClickItem,
    toggleExpand,
    totalFilters,
    showFilters,
    setShowFilters,
    sort,
    setSort,
    sorts,
  } = useArtworksAction({ catalogData, setCatalogData, gallery, filters })

  const {
    form,
    setForm,
    onSubmit,
    handleShow,
    handleClose,
    modal,
    success,
    error,
    errorMessage,
    onReCAPTCHAChange,
    recaptchaRef,
    loadingContact,
  } = useContactAction(gallery)

  const LoadedElements = () => {
    if (loading) return <Loader />

    const items = catalogData.data.map((item) => ({
      to: linksBuilder.artworks(item.gallery?.domain, item.id, item.title),
      href: "/galleries/[id]/artworks/[artwork]",
      title: getArtworkTitle(item.title),
      image: item.image,
      subtitle: getArtworkSubtitle(item.artistname, item.gallery?.name),
      subSubTitle: getArtworkSize(item.width, item.height),
      sold: item.sold,
      price: item.price
        ? item.gallery.currency.symbol + " " + formatPrice(item.price)
        : null,
      textAlign: "left",
    }))

    return <Masonry id="artworks" items={items} />
  }

  const Header = () => {
    if (gallery) {
      return (
        <GalleryHeader galleryDetails={gallery} onClickContact={handleShow} />
      )
    }

    return (
      <Row>
        <Col xs={12} md={6}>
          <HeadingTitle title="Artworks">
            Discover fine art paintings, posters, prints, drawings and
            photographs from the comfort of your home.
          </HeadingTitle>
        </Col>
      </Row>
    )
  }

  const Filters = () => (
    <LeftSide
      search={search}
      collapses={collapses}
      clearFilters={clearFilters}
      onClickItem={onClickItem}
      toggleExpand={toggleExpand}
      totalFilters={totalFilters}
      filters={filters}
    />
  )

  // Discover Profile statement; fall back to the Artist Info statement when empty.
  const statement = gallery && (gallery.discover_statement || gallery.statement)

  return (
    <>
      <SeoTags
        title="Artworks | Discover ArtPlacer"
        description="Discover fine art paintings, posters, prints and photographs from the comfort of your home."
        url="https://discover.artplacer.com/artworks"
        canonical={`https://discover.artplacer.com/${
          gallery ? `galleries/${gallery.domain}/` : ""
        }artworks`}
      />
      <HelmetTitle title="Artworks" />
      <Wrapper>
        <Container>
          <Header />
          <Row className="justify-content-center text-black fs-15px fs-mobile-14px">
            {profile.embedded && statement && (
              <>
                <Col lg={8} md={8} sm={10} xs={11} className="my-sm-1 my-md-4">
                  <p
                    className={`mb-0 word-wrap-break font-weight-light text-black lh-17 ws-pw text-container ${
                      statement.length > 1000 ? "two-columns" : ""
                    }`}>
                    {statement}
                  </p>
                </Col>
                <hr className="w-100 mx-3 mt-4 mb-2 pb-0" />
              </>
            )}
            {profile.embedded &&
              gallery.collections.length > 0 &&
              gallery.collections.length < 80 && (
                <>
                  <Row className="mx-2 my-3 w-100">
                    {gallery.collections.map((c) => (
                      <Col
                        sm="4"
                        md="3"
                        className="text-capitalize text-truncate font-weight-light mb-3 fs-15px">
                        <Link
                          style={{
                            color: "black",
                            lineHeight: "1.2",
                            whiteSpace: "pre",
                          }}
                          href={linksBuilder.collections(
                            gallery.id,
                            c.collection,
                          )}>
                          {c.label ?? c.collection}
                        </Link>
                      </Col>
                    ))}
                  </Row>
                  <hr className="w-100 mx-3 mt-2 mb-4 pb-0" />
                </>
              )}
          </Row>
          <Row className="py-4">
            {!profile.embedded && (
              <Col xs={12} sm={12} md={2} className="d-none d-md-block">
                <Filters />
              </Col>
            )}
            <Col xs={12} sm={12} md={!profile.embedded ? 10 : 12}>
              <SearchSortHeader
                hideSearch={true}
                title=""
                sort={sort}
                setSort={setSort}
                sorts={sorts}>
                {!profile.embedded && (
                  <FilterButton onClick={() => setShowFilters(true)} />
                )}
              </SearchSortHeader>
              <LoadedElements />
            </Col>
          </Row>

          <GalleryContactModal
            form={form}
            setForm={setForm}
            galleryDetails={gallery}
            modal={modal}
            onSubmit={onSubmit}
            handleClose={handleClose}
            error={error}
            errorMessage={errorMessage}
            success={success}
            recaptchaRef={recaptchaRef}
            onReCAPTCHAChange={onReCAPTCHAChange}
          />

          {gallery && gallery.domain !== "pinta-baphoto" && (
            <NewsletterGalleries gallery={gallery} />
          )}
        </Container>
      </Wrapper>
      {showFilters && (
        <MobileMenu className={styles["mobile-menu-container"]}>
          <div
            className={classnames(
              styles["filter-mobile"],
              "position-relative",
            )}>
            <Filters />
            <ApplyButton onClick={() => setShowFilters(false)} />
          </div>
        </MobileMenu>
      )}
    </>
  )
}

export default Artworks
